import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Grid, User, Calendar, CreditCard, Loader2, Check } from "lucide-react";
import Header from '../../components/ui/Header';
import ServiceSelector from "./components/ServiceSelector";
import BookingDetails from "./components/BookingDetails";
import DateTimeSelector from "./components/DateTimeSelector";
import UnifiedPaymentConfirmation from "../../components/booking/UnifiedPaymentConfirmation";
import UnifiedBookingSuccess from "../../components/booking/UnifiedBookingSuccess";
import { wellnessService } from '../../services/wellnessService';

const BookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef(null);
  const paymentConfirmationRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    services: [],
    details: {
      name: "",
      email: "",
      phone: "",
      gender: "",
      notes: "",
      reminderPreferences: [],
    },
    dateTime: {
      date: null,
      timeSlot: null,
    },
    payment: {
      method: "",
      status: "pending",
    },
  });
  const [preSelectedService, setPreSelectedService] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentValidationState, setPaymentValidationState] = useState(false);

  // RE-ENABLED - Check for pre-selected service from URL parameters (SAFE - only runs when URL changes)
  useEffect(() => {
    const fetchPreSelectedService = async () => {
      const searchParams = new URLSearchParams(location.search);
      const serviceSlug = searchParams.get('service');

      if (serviceSlug) {
        try {
          console.log('🔍 Fetching pre-selected service:', serviceSlug);
          const service = await wellnessService.getWellnessServiceBySlug(serviceSlug);

          if (service) {
            console.log('✅ Pre-selected service found:', service.name);

            // Transform service to match booking format
            const transformedService = {
              id: service.id,
              slug: service.slug,
              name: service.name,
              duration_minutes: service.duration_minutes,
              therapists_required: service.therapists_required,
              base_price: service.base_price,
              category: service.category
            };

            setPreSelectedService(transformedService);

            // Pre-populate the services in booking data
            setBookingData(prev => ({
              ...prev,
              services: [transformedService]
            }));

            // Skip to Details step (step 2)
            setCurrentStep(2);
          }
        } catch (error) {
          console.error('❌ Error fetching pre-selected service:', error);
        }
      }
    };

    fetchPreSelectedService();
  }, [location.search]);

  // RE-ENABLED - Scroll to top whenever step changes (SAFE - just scrolls the page)
  useEffect(() => {
    if (contentRef?.current) {
      contentRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStep]);



  // Steps without therapist selection
  const steps = [
    { id: 1, title: "Services", component: ServiceSelector, icon: Grid },
    { id: 2, title: "Details", component: BookingDetails, icon: User },
    { id: 3, title: "Date & Time", component: DateTimeSelector, icon: Calendar },
    { id: 4, title: "Payment", component: UnifiedPaymentConfirmation, icon: CreditCard },
    { id: 5, title: "Confirmation", component: UnifiedBookingSuccess, icon: CreditCard },
  ];

  const CurrentStepComponent = steps?.find(
    (step) => step?.id === currentStep
  )?.component;

  const handleNext = async () => {
    console.log(`📍 handleNext called - currentStep: ${currentStep}, isProcessingPayment: ${isProcessingPayment}`);

    // For step 4 (Payment), trigger the confirmation handler from the child component
    // But only if not already processing (to avoid infinite loop)
    if (currentStep === 4 && paymentConfirmationRef.current && !isProcessingPayment) {
      console.log('💳 Triggering payment confirmation...');
      setIsProcessingPayment(true);
      try {
        await paymentConfirmationRef.current.handleConfirmBooking();
        // SUCCESS - Move to next step (confirmation page)
        console.log('✅ Booking confirmed, moving to step 5');
        setCurrentStep(5);
      } catch (error) {
        console.error('❌ Error during booking confirmation:', error);
        // Don't move to next step on error
      } finally {
        setIsProcessingPayment(false);
      }
      return;
    }

    // If already processing or not on step 4, just move to next step
    console.log(`➡️ Moving to next step: ${currentStep} -> ${currentStep + 1}`);
    if (currentStep < steps?.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    // If we have a pre-selected service and we're on step 2, don't go back to step 1
    if (preSelectedService && currentStep === 2) {
      // Navigate back to home page or service universe
      navigate('/#service-universe');
      return;
    }

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDataUpdate = (stepData) => {
    setBookingData((prev) => ({
      ...prev,
      ...stepData,
    }));
  };

  // Add handler for service selection
  const handleServiceUpdate = (services) => {
    setBookingData((prev) => ({
      ...prev,
      services: services,
    }));
  };

  const handleValidationChange = (isValid) => {
    setPaymentValidationState(isValid);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return bookingData?.services?.length > 0;
      case 2:
        // Email is optional - only require name, phone, and gender
        return (bookingData?.details?.name &&
          bookingData?.details?.phone && bookingData?.details?.gender);
      case 3:
        return bookingData?.dateTime?.date && bookingData?.dateTime?.timeSlot;
      case 4:
        // Use state that gets updated when checkbox/payment method changes
        return paymentValidationState;
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div ref={contentRef} className="container mx-auto px-2 sm:px-4 py-4 sm:py-6 md:py-8">
        {/* Progress Indicator */}
        <div className="mt-[60px] mb-6 sm:mb-8">
          <div className="max-w-4xl mx-auto bg-background border-2 border-purple-200 rounded-lg shadow-md p-4 sm:p-6 md:p-8">
            {/* Show selected service info if pre-selected */}
            {preSelectedService && (
              <div className="mb-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">Selected Service:</span> {preSelectedService.name}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-2">
              {steps?.slice(0, 4)
                ?.filter(step => !preSelectedService || step.id !== 1) // Hide Services step if pre-selected
                ?.map((step, index, filteredSteps) => {
                  const StepIcon = step?.icon;
                  return (
                    <React.Fragment key={step?.id}>
                      <div className="flex items-center w-full sm:w-auto sm:flex-1">
                        <div className="flex flex-col items-center w-full">
                          <div
                            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-semibold transition-all flex-shrink-0 ${currentStep >= step?.id
                              ? "bg-purple-600 text-white" : "bg-gray-200 text-gray-500"
                              }`}
                          >
                            <StepIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                          </div>
                          <span
                            className={`mt-2 text-xs sm:text-sm font-medium transition-colors text-center whitespace-nowrap ${currentStep >= step?.id
                              ? "text-purple-600" : "text-gray-500"
                              }`}
                          >
                            {step?.title}
                          </span>
                        </div>
                      </div>
                      {index < filteredSteps.length - 1 && (
                        <div className="hidden sm:flex items-center sm:w-16 md:w-24">
                          <div
                            className={`h-1 w-full transition-all ${currentStep > step?.id ? "bg-purple-600" : "bg-gray-200"
                              }`}
                          />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="max-w-4xl mx-auto glass-card p-4 sm:p-6 md:p-8">
          {CurrentStepComponent && (
            <>
              {currentStep === 1 && !preSelectedService ? (
                <ServiceSelector
                  selectedServices={bookingData?.services}
                  onServiceToggle={handleServiceUpdate}
                  onNext={handleNext}
                />
              ) : currentStep === 4 ? (
                <CurrentStepComponent
                  ref={paymentConfirmationRef}
                  bookingData={bookingData}
                  onDataUpdate={handleDataUpdate}
                  onBack={handleBack}
                  onValidationChange={handleValidationChange}
                  bookingType="therapy"
                />
              ) : (
                <CurrentStepComponent
                  bookingData={bookingData}
                  onDataUpdate={handleDataUpdate}
                  onNext={handleNext}
                  onBack={handleBack}
                  bookingType={(currentStep === 5) ? "therapy" : undefined}
                />
              )}
            </>
          )}

          {/* Navigation Buttons */}
          {currentStep < 5 && (
            <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t">
              <button
                onClick={handleBack}
                disabled={currentStep === 1 || isProcessingPayment}
                className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition-all text-sm sm:text-base ${currentStep === 1 || isProcessingPayment
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
              >
                Back
              </button>
              <button
                onClick={handleNext}
                disabled={!canProceed() || isProcessingPayment}
                className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition-all text-sm sm:text-base flex items-center justify-center ${canProceed() && !isProcessingPayment
                  ? "bg-purple-600 text-white hover:bg-purple-700" : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
              >
                {isProcessingPayment ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : currentStep === 4 ? (
                  <>
                    Confirm Booking
                    <Check className="w-5 h-5 ml-2" />
                  </>
                ) : (
                  "Next"
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingPage;